import { ref, computed, readonly } from "vue";

/**
 * Installation PWA « depuis un bouton de l'app ».
 *
 * - Chromium (Android Chrome, desktop Chrome/Edge) : l'événement
 *   `beforeinstallprompt` est capturé au démarrage ; `promptInstall()` déclenche
 *   l'installation native.
 * - iOS Safari / Firefox : pas d'install programmatique → on expose `isIos()`
 *   pour afficher des instructions manuelles (Partager → Sur l'écran d'accueil).
 *
 * L'événement peut arriver AVANT le montage du 1er composant : on branche donc
 * les listeners au démarrage via `setupInstallPrompt()` (appelé dans index.ts).
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const canInstall = ref(false); // Chromium + installable + pas déjà installée
const installed = ref(false);

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS (Safari en mode "app" depuis l'écran d'accueil)
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  // iPadOS récent se présente comme "Macintosh" → on le détecte via le tactile.
  return (
    /iphone|ipad|ipod/i.test(ua) ||
    (/Macintosh/.test(ua) && "ontouchend" in window)
  );
}

let bound = false;
/** À appeler une fois au démarrage (index.ts) pour ne jamais rater l'événement. */
export function setupInstallPrompt(): void {
  if (bound || typeof window === "undefined") return;
  bound = true;
  installed.value = isStandalone();

  window.addEventListener("beforeinstallprompt", (e) => {
    // Empêche la mini-barre Chrome : on pilotera l'install depuis notre bouton.
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    canInstall.value = !installed.value;
  });

  window.addEventListener("appinstalled", () => {
    installed.value = true;
    canInstall.value = false;
    deferred = null;
  });
}

export function useInstallPrompt() {
  /**
   * Déclenche l'installation native. Retourne :
   * - 'accepted' / 'dismissed' : l'utilisateur a répondu au prompt Chromium
   * - 'unavailable' : pas de prompt dispo (iOS, Firefox, déjà installée…)
   */
  async function promptInstall(): Promise<
    "accepted" | "dismissed" | "unavailable"
  > {
    if (!deferred) return "unavailable";
    await deferred.prompt();
    const choice = await deferred.userChoice;
    deferred = null;
    canInstall.value = false;
    return choice.outcome;
  }

  // Faut-il proposer l'installation ? Oui si pas déjà installée ET (prompt
  // Chromium dispo OU iOS, où l'on montrera des instructions manuelles).
  const canOfferInstall = computed(
    () => !installed.value && (canInstall.value || isIos())
  );

  return {
    canInstall: readonly(canInstall),
    installed: readonly(installed),
    canOfferInstall,
    isIos,
    isStandalone,
    promptInstall,
  };
}
